import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import {
	AppBar,
	Box,
	Chip,
	IconButton,
	Menu,
	MenuItem,
	Toolbar,
	Tooltip,
	Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import { LANGUAGES } from "@repo/i18n/constants";
import { handleChangePassword, useMe } from "@repo/shared/components";
import { clearImpersonation, getImpersonation } from "@repo/server-api/impersonation";
import { useTranslation } from "next-i18next";
import React, { forwardRef, useEffect, useMemo, useState } from "react";

interface HeaderProps {
	isDesktop: boolean;
	onMenuToggle: () => void;
	menuToggleValue?: boolean;
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
	zIndex: theme.zIndex.drawer + 1,
	backgroundColor: theme.palette.background.default,
	color: theme.palette.primary.main,
}));

const Header = forwardRef<HTMLDivElement, HeaderProps>(
	({ isDesktop, onMenuToggle, menuToggleValue }, ref) => {
		const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
		const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
		const { keycloak } = useKeycloak();
		const { i18n, t } = useTranslation();

		// Check if user is impersonating via SessionStorage
		const impersonationInfo = useMemo(() => {
			const impersonation = getImpersonation();
			if (!impersonation) return null;

			return {
				isImpersonated: true,
				impersonatorUsername: impersonation.adminUsername,
			};
		}, []); // Check on every render

		const isImpersonated = impersonationInfo?.isImpersonated || false;

		const handleStopImpersonation = () => {
			handleCloseUserMenu();

			const impersonation = getImpersonation();
			if (!impersonation) return;

			const startUrl = impersonation.startUrl;
			clearImpersonation();

			// Close this window and return admin to their tab
			window.close();

			// If window.close() didn't work (popup blocker), redirect
			setTimeout(() => {
				if (!window.closed) {
					window.location.href = startUrl;
				}
			}, 100);
		};

		const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
			setAnchorElUser(event.currentTarget);
		};

		const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
			setAnchorElLang(event.currentTarget);
		};

		const handleCloseUserMenu = () => setAnchorElUser(null);
		const handleCloseLangMenu = () => setAnchorElLang(null);
		const handleLanguage = (language: string) => () => {
			localStorage.setItem("i18nextLng", language);
			handleCloseLangMenu();
		};

		useEffect(() => {
			i18n.changeLanguage(localStorage.getItem("i18nextLng") || "de");
		}, [localStorage.getItem("i18nextLng")]);

		const contact = useMe();

		const theme = useTheme();

		return (
			<StyledAppBar position="fixed" ref={ref}>
				<Toolbar style={{ paddingLeft: 0, maxHeight: 64, overflow: "hidden" }}>
					{!isDesktop && (
						<IconButton color="inherit" edge="start" onClick={onMenuToggle} sx={{ mr: 2 }}>
							<MenuIcon />
						</IconButton>
					)}

					<Box
						sx={{
							width: theme.sidebar.width,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: theme.shadows[4],
							boxSizing: "border-box",
							height: "70px",
							cursor: "pointer",
						}}
						className="left"
						onClick={onMenuToggle}>
						{menuToggleValue ? (
							<>
								<ArrowBackIos />
								<ArrowBackIos />
								<ArrowBackIos />
							</>
						) : (
							<>
								<ArrowForwardIos />
								<ArrowForwardIos />
								<ArrowForwardIos />
							</>
						)}
					</Box>

					<Box sx={{ flexGrow: 1 }} />

					<Box className="right">
						<Box className="left">
							<Tooltip title="Languages">
								<IconButton className="font language" onClick={handleOpenLangMenu} sx={{ p: 0 }}>
									{t("lang")}
								</IconButton>
							</Tooltip>
						</Box>

						<Menu
							sx={{ mt: "45px" }}
							anchorEl={anchorElLang}
							open={Boolean(anchorElLang)}
							onClose={handleCloseLangMenu}>
							{LANGUAGES.map((language) => (
								<MenuItem key={language.code} onClick={handleLanguage(language.code)}>
									<Typography textAlign="center">{language.label}</Typography>
								</MenuItem>
							))}
						</Menu>

						<Tooltip title="Open settings">
							<Box display="flex" alignItems="center" gap={1}>
								<IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
									<AccountCircleIcon className="font avatar" />
								</IconButton>
								{contact && contact.displayName && (
									<Typography className="font avatarName">{contact.displayName}</Typography>
								)}
								{isImpersonated && (
									<Chip
										label={t("impersonated-by", { username: impersonationInfo?.impersonatorUsername || "Unknown" })}
										color="warning"
										size="small"
										sx={{ height: 24, fontSize: "0.75rem" }}
									/>
								)}
							</Box>
						</Tooltip>

						<Menu
							sx={{ mt: "45px" }}
							anchorEl={anchorElUser}
							open={Boolean(anchorElUser)}
							onClose={handleCloseUserMenu}>
							{!isImpersonated && (
								<MenuItem onClick={() => handleChangePassword()}>
									<Typography textAlign="center">{t("change-password")}</Typography>
								</MenuItem>
							)}
							{isImpersonated ? (
								<MenuItem onClick={handleStopImpersonation}>
									<PersonOffIcon sx={{ mr: 1 }} />
									<Typography textAlign="center">{t("stop-impersonation")}</Typography>
								</MenuItem>
							) : (
								<MenuItem onClick={() => keycloak.logout()}>
									<Typography textAlign="center">{t("logout")}</Typography>
								</MenuItem>
							)}
						</Menu>
					</Box>
				</Toolbar>
			</StyledAppBar>
		);
	},
);

Header.displayName = "Header";
export default Header;
